/**
 * Where a Live now block's stream may come from (ADR 0033, amendments of 2026-10-03). The block stores the provider's
 * own page address, checked here against a pattern anchored at both ends, and the renderer builds the frame address
 * from the identifier inside it, so nobody can place an arbitrary frame on a church's site and a pasted address is
 * never embedded as it was typed.
 *
 * The providers are every one Planning Center's Publishing lists ("Youtube, Vimeo, Resi, Boxcast", help article
 * 161071), and Facebook, which Planning Center does not support. Each accepts an address that stays the same from week
 * to week where the provider has one (Planning Center's recommended setup):
 *
 * - YouTube: a channel's live address (`youtube.com/channel/UC…/live`) or a handle (`youtube.com/@Church/live`, with
 *   the channel id found when staff save the block); or one video (`watch?v=`, `youtu.be/`, `/live/<id>`). Framed
 *   through `youtube-nocookie.com`.
 * - Vimeo: a recurring event (`vimeo.com/event/<id>`) or one video (`vimeo.com/<id>`).
 * - Resi: a web channel's player address (`control.resi.io/webplayer/video.html?id=<uuid>`), which stays the same;
 *   framed at the same address with `iframe=1`.
 * - BoxCast: a channel (`boxcast.tv/channel/<id>`, framed so it plays the current or next broadcast), or one broadcast
 *   (`boxcast.tv/view/<id>` or `/view-embed/<id>`).
 * - Facebook: one video (`facebook.com/<page>/videos/<id>`, `facebook.com/watch/?v=<id>`, `/watch/live/?v=<id>`),
 *   framed through Facebook's video plugin; or a page's live address (`facebook.com/<page>/live`), which is a link
 *   only: Facebook has no frame for "whatever the page is streaming".
 *
 * Only YouTube and Vimeo can be chosen today (`AVAILABLE_LIVESTREAM_PROVIDERS`, ADR 0033's third amendment): Resi,
 * BoxCast and Facebook are "Coming soon" until counsel answers. Everything below still serves all five.
 *
 * Kept apart from `livestream.ts` so the block contracts can import the patterns without a cycle.
 */

const YOUTUBE_ID = "[A-Za-z0-9_-]{6,20}";
const YOUTUBE_CHANNEL = "UC[A-Za-z0-9_-]{22}";
/** A YouTube handle: 3 to 30 letters, digits, underscores, hyphens and full stops (ASCII only). */
const YOUTUBE_HANDLE = "[A-Za-z0-9_.-]{3,30}";
/** What may follow the identifier: a query or a fragment (`&t=…`, `?si=…`), never another path. */
const TAIL = "(?:[?&#][^\\s]*)?$";
const UUID = "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";
/** A Resi player option such as `iframe=1` or `background=0`: letters, then letters and digits. */
const RESI_OPTION = "[A-Za-z]{1,40}=[A-Za-z0-9]{0,40}";
const BOXCAST_ID = "[A-Za-z0-9_-]{4,64}";
const FACEBOOK_PAGE = "[A-Za-z0-9.-]{1,80}";
const FACEBOOK_VIDEO = "\\d{5,25}";

/** A channel id as YouTube writes it: UC and 22 more characters. */
export const YOUTUBE_CHANNEL_ID = new RegExp(`^${YOUTUBE_CHANNEL}$`);

const YOUTUBE_HANDLE_ADDRESS = new RegExp(
  `^https://(?:www\\.|m\\.)?youtube\\.com/@(${YOUTUBE_HANDLE})(?:/live|/streams|/featured)?/?${TAIL}`,
);

export const LIVESTREAM_PROVIDERS = {
  youtube: new RegExp(
    `^https://(?:www\\.|m\\.)?(?:youtube\\.com/(?:watch\\?v=${YOUTUBE_ID}|live/${YOUTUBE_ID}|channel/${YOUTUBE_CHANNEL}(?:/live|/streams)?/?|@${YOUTUBE_HANDLE}(?:/live|/streams|/featured)?/?)|youtu\\.be/${YOUTUBE_ID})${TAIL}`,
  ),
  vimeo: /^https:\/\/(?:www\.)?vimeo\.com\/(?:event\/)?\d{4,12}\/?(?:[?#][^\s]*)?$/,
  resi: new RegExp(
    `^https://control\\.resi\\.io/webplayer/video\\.html\\?(?:${RESI_OPTION}&){0,10}id=${UUID}(?:&${RESI_OPTION}){0,10}$`,
  ),
  boxcast: new RegExp(
    `^https://(?:www\\.)?boxcast\\.tv/(?:channel|view|view-embed)/${BOXCAST_ID}/?${TAIL}`,
  ),
  facebook: new RegExp(
    `^https://(?:www\\.|m\\.)?facebook\\.com/(?:${FACEBOOK_PAGE}/videos/(?:[A-Za-z0-9.-]{1,80}/)?${FACEBOOK_VIDEO}/?|watch/(?:live/)?\\?v=${FACEBOOK_VIDEO}|${FACEBOOK_PAGE}/live(?:_videos)?/?)${TAIL}`,
  ),
} as const;

export type LivestreamProvider = keyof typeof LIVESTREAM_PROVIDERS;

/** The providers in the order the editor offers them. */
export const LIVESTREAM_PROVIDER_KEYS = [
  "youtube",
  "vimeo",
  "resi",
  "boxcast",
  "facebook",
] as const;

export const LIVESTREAM_PROVIDER_LABELS: Readonly<Record<LivestreamProvider, string>> = {
  youtube: "YouTube",
  vimeo: "Vimeo",
  resi: "Resi",
  boxcast: "BoxCast",
  facebook: "Facebook",
};

/**
 * The providers a church can choose today (owner decision of 2026-10-03, ADR 0033's third amendment). YouTube (through
 * its no-cookie domain) and Vimeo only; Resi, BoxCast and Facebook show in the editor as "Coming soon" until counsel
 * answers whether HoW should load third-party players that may set their own cookies or track visitors (the legal
 * brief, question 16; data decision D26). Their patterns, frames and help stay in this file, tested, so enabling one
 * is a change to this set and nothing else: the contract, the editor, the public site and the help follow it.
 */
export const AVAILABLE_LIVESTREAM_PROVIDERS: ReadonlySet<LivestreamProvider> =
  new Set<LivestreamProvider>(["youtube", "vimeo"]);

/** Whether a church can choose this provider today; the others are "Coming soon". */
export function isLivestreamProviderAvailable(provider: LivestreamProvider): boolean {
  return AVAILABLE_LIVESTREAM_PROVIDERS.has(provider);
}

/** The providers a church can choose today, as a sentence: "YouTube or Vimeo". */
export function availableLivestreamProviderNames(): string {
  const names = LIVESTREAM_PROVIDER_KEYS.filter(isLivestreamProviderAvailable).map(
    (provider) => LIVESTREAM_PROVIDER_LABELS[provider],
  );
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} or ${names[names.length - 1] ?? ""}`;
}

/** Whether a pasted address is one this provider serves a stream from. */
export function isLivestreamAddress(provider: LivestreamProvider, url: string): boolean {
  return LIVESTREAM_PROVIDERS[provider].test(url);
}

/** The handle in a YouTube handle address (`https://www.youtube.com/@Church/live` → `Church`), else null. */
export function youtubeHandle(url: string): string | null {
  return YOUTUBE_HANDLE_ADDRESS.exec(url)?.[1] ?? null;
}

/**
 * The address a provider plays the stream at inside a frame, built from the identifier in the page address the church
 * pasted; null for an address that is not the provider's, or one the provider will not frame (a Facebook page's live
 * address, a YouTube handle whose channel was not found). YouTube through its no-cookie domain, as the embed block
 * does. Nothing asks the player to start on its own: a visitor presses play.
 *
 * `channelId` is the YouTube channel a handle address was found to name when staff saved the block (the
 * `resolve-youtube-channel` function), so a page never asks YouTube anything while a visitor waits.
 */
export function livestreamEmbedSource(
  provider: LivestreamProvider,
  url: string,
  channelId?: string,
): string | null {
  if (!isLivestreamAddress(provider, url)) return null;
  switch (provider) {
    case "youtube": {
      if (youtubeHandle(url) !== null) {
        return channelId !== undefined && YOUTUBE_CHANNEL_ID.test(channelId)
          ? `https://www.youtube-nocookie.com/embed/live_stream?channel=${channelId}`
          : null;
      }
      const channel = new RegExp(`youtube\\.com/channel/(${YOUTUBE_CHANNEL})`).exec(url)?.[1];
      if (channel !== undefined) {
        return `https://www.youtube-nocookie.com/embed/live_stream?channel=${channel}`;
      }
      const video = new RegExp(`(?:v=|youtu\\.be/|/live/)(${YOUTUBE_ID})`).exec(url)?.[1];
      return video === undefined ? null : `https://www.youtube-nocookie.com/embed/${video}`;
    }
    case "vimeo": {
      const event = /vimeo\.com\/event\/(\d{4,12})/.exec(url)?.[1];
      if (event !== undefined) return `https://vimeo.com/event/${event}/embed`;
      const video = /vimeo\.com\/(\d{4,12})/.exec(url)?.[1];
      return video === undefined ? null : `https://player.vimeo.com/video/${video}`;
    }
    case "resi": {
      const id = new RegExp(`[?&]id=(${UUID})`).exec(url)?.[1];
      return id === undefined
        ? null
        : `https://control.resi.io/webplayer/video.html?id=${id.toLowerCase()}&iframe=1`;
    }
    case "boxcast": {
      const match = new RegExp(`boxcast\\.tv/(channel|view|view-embed)/(${BOXCAST_ID})`).exec(url);
      if (match === null) return null;
      const kind = match[1];
      const id = match[2] ?? "";
      // A channel plays its current broadcast, else the next one: the address that stays the same each week.
      return kind === "channel"
        ? `https://boxcast.tv/channel/${id}?defaultVideo=next`
        : `https://boxcast.tv/view-embed/${id}`;
    }
    case "facebook": {
      const video = facebookVideoAddress(url);
      return video === null
        ? null
        : `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(video)}&show_text=false`;
    }
  }
}

/** A Facebook video's own address, rebuilt from its parts; null for a page's live address, which names no video. */
function facebookVideoAddress(url: string): string | null {
  const onPage = new RegExp(
    `facebook\\.com/(${FACEBOOK_PAGE})/videos/(?:[A-Za-z0-9.-]{1,80}/)?(${FACEBOOK_VIDEO})`,
  ).exec(url);
  if (onPage !== null) {
    return `https://www.facebook.com/${onPage[1] ?? ""}/videos/${onPage[2] ?? ""}/`;
  }
  const watch = new RegExp(`facebook\\.com/watch/(?:live/)?\\?v=(${FACEBOOK_VIDEO})`).exec(
    url,
  )?.[1];
  return watch === undefined ? null : `https://www.facebook.com/watch/?v=${watch}`;
}

/**
 * An address the block can only link to, so it draws the "Watch live" button whatever its layout: a Facebook page's
 * live address, or a YouTube handle with no channel found.
 */
export function isLinkOnlyAddress(
  provider: LivestreamProvider,
  url: string,
  channelId?: string,
): boolean {
  return (
    isLivestreamAddress(provider, url) && livestreamEmbedSource(provider, url, channelId) === null
  );
}

/**
 * Where the "Watch live on …" link goes: the provider's own page for the stream. An address that is itself a frame
 * (BoxCast's `view-embed`) is turned back into its page; anything else is the address the church pasted, which the
 * contract has already checked.
 */
export function livestreamWatchAddress(provider: LivestreamProvider, url: string): string {
  if (provider === "boxcast") {
    const id = new RegExp(`boxcast\\.tv/view-embed/(${BOXCAST_ID})`).exec(url)?.[1];
    if (id !== undefined) return `https://boxcast.tv/view/${id}`;
  }
  return url;
}

/**
 * What staff pasted, reduced to an address: an embed code (`<iframe src="…">`) becomes the address in its `src`, and
 * Facebook's plugin address becomes the video address inside it. Staff often have only the embed code (Resi and
 * BoxCast hand it out first); nothing else in the code is kept, so a pasted script or attribute never reaches a page,
 * and the address that is kept must still pass the contract.
 */
export function streamAddressFromPaste(input: string): string {
  const trimmed = input.trim();
  const src = /<iframe\b[^>]*\bsrc\s*=\s*["']([^"']+)["']/i.exec(trimmed)?.[1];
  const address = (src ?? trimmed).replace(/&amp;/g, "&").trim();
  const plugin =
    /^https:\/\/(?:www\.)?facebook\.com\/plugins\/video\.php\?(?:[^#]*&)?href=([^&#]+)/.exec(
      address,
    )?.[1];
  if (plugin !== undefined) {
    try {
      return decodeURIComponent(plugin);
    } catch {
      return address;
    }
  }
  return address;
}

/** Which provider an address belongs to, for an editor that guesses from a paste; null when none claims it. */
export function livestreamProviderOf(url: string): LivestreamProvider | null {
  return LIVESTREAM_PROVIDER_KEYS.find((provider) => isLivestreamAddress(provider, url)) ?? null;
}
